/* =======================================================================
   assets/quiz-petgently-v2.js

   PetGently Joint Care Quiz runtime - short form (v2).

   CHANGES FROM V1
   ---------------
   - Result-type routing removed. resolveResultType / severity scoring /
     buildResultUrl param spray are gone. Completion redirects to one
     product / offer URL, clean, with optional UTM pass-through.
   - Landing screen removed. Block 0 carries the hero intro and is a
     live question; the sticky header appears from block 1 onward.
   - Breed dropdown and 1-5 slider bindings removed.
   - Interstitial no longer counts as a step in the progress counter.

   Answers still ride to the order as cart attributes and to the Apps
   Script endpoint on every step. The endpoint payload keeps its original
   key set so the existing Sheet columns do not shift; keys belonging to
   removed questions are simply sent empty. Session ids are prefixed
   pg2_ so v2 rows are distinguishable from v1 rows in the same Sheet.
   ======================================================================= */

(function () {
  'use strict';

  function init() {
    var root = document.querySelector('[data-quiz-section]');
    if (!root) return;

    var debug = root.dataset.debug === 'true';
    var log = debug
      ? function () { console.log.apply(console, ['[PetGentlyQuizV2]'].concat([].slice.call(arguments))); }
      : function () {};

    // ----- DOM refs -----
    var blocks = Array.prototype.slice.call(root.querySelectorAll('.quiz-pg-block'));
    if (blocks.length === 0) { log('No blocks found.'); return; }

    var headerEl = root.querySelector('[data-quiz-header]');
    var backBtn = root.querySelector('[data-quiz-back]');
    var stepLabel = root.querySelector('[data-quiz-step]');
    var progressFill = root.querySelector('[data-quiz-progress]');

    // ----- Config (data attributes) -----
    var offerUrl = root.dataset.offerUrl || '/';
    var forwardUtm = root.dataset.forwardUtm === 'true';
    var storageKey = root.dataset.storageKey || 'quiz:petgently-v2';

    // Step types that count as real, trackable progress (skip affirmations/loading/interstitial).
    var STEP_TYPES = ['text-input', 'quiz-question', 'dual-select'];
    var TRACKABLE_STEP_TYPES = ['text-input', 'quiz-question', 'dual-select', 'interstitial'];

    // ----- State -----
    var sessionId = generateSessionId();
    var startedAt = Date.now();
    var history = [];
    var currentIdx = -1;
    var isRedirecting = false;

    // answers shape:
    //   { block_idx: { type, block_id, selections: [{ value, text, severity, route_flag, previous_brand }] } }
    var answers = {};

    // Personalization vars (tokens in copy + cart attributes + tracking payload)
    var personalization = {
      dog_name: '',
      pronoun: 'he',
      sex: '',
      neuter: '',
      age: '',
      weight_bracket: '',
      q5_symptoms_top_2: '',
      value_anchor: '',
      previous_brand: '',
      tried_category: '',
      drug_interaction_flag: false
    };

    log('Initialized', { blockCount: blocks.length, offerUrl: offerUrl });

    var resumed = rehydrate();
    bindAllBlocks();

    if (!resumed) {
      // Fresh session: block 0 is already active server-side (hero + first question).
      currentIdx = 0;
    }

    var startBlock = blocks[currentIdx];
    applyPersonalization(startBlock);
    if (startBlock.dataset.blockType === 'interstitial') selectInterstitialVariant(startBlock);
    startBlock.dispatchEvent(new CustomEvent('quiz:block-shown', { detail: { idx: currentIdx } }));
    if (!resumed) trackProgress(startBlock);
    persist();

    updateChrome();

    /* ======== Block binding ======== */

    function bindAllBlocks() {
      blocks.forEach(function (block, idx) {
        switch (block.dataset.blockType) {
          case 'quiz-question': bindQuizQuestion(block, idx); break;
          case 'text-input':    bindTextInput(block, idx); break;
          case 'dual-select':   bindDualSelect(block, idx); break;
          case 'interstitial':  bindInterstitial(block, idx); break;
          case 'affirmation':   bindAffirmation(block, idx); break;
          case 'loading':       bindLoading(block, idx); break;
        }
      });

      if (backBtn) backBtn.addEventListener('click', goBack);
    }

    function bindQuizQuestion(block, idx) {
      var labels = Array.prototype.slice.call(block.querySelectorAll('.qpg-arow'));
      var inputs = Array.prototype.slice.call(block.querySelectorAll('.qpg-answer__input'));
      var inputType = inputs.length > 0 ? inputs[0].type : 'radio';
      var nextBtn = block.querySelector('.qpg-next');

      labels.forEach(function (label) {
        var input = label.querySelector('.qpg-answer__input');
        if (!input) return;
        input.addEventListener('change', function () {
          handleQuestionAnswer(block, idx, label, input, inputType, nextBtn);
        });
      });

      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          finalizeQuestionAnswer(block, idx);
          advanceFrom(idx);
        });
      }
    }

    function handleQuestionAnswer(block, idx, label, input, inputType, nextBtn) {
      label.classList.add('qpg-press--pressed');
      setTimeout(function () { label.classList.remove('qpg-press--pressed'); }, 240);

      if (inputType === 'radio') {
        block.querySelectorAll('.qpg-arow').forEach(function (l) { l.classList.remove('qpg-arow--selected'); });
        label.classList.add('qpg-arow--selected');
        setTimeout(function () {
          finalizeQuestionAnswer(block, idx);
          advanceFrom(idx);
        }, parseInt(getCssVar('--auto-advance-delay'), 10) || 360);
      } else {
        label.classList.toggle('qpg-arow--selected', input.checked);
        if (nextBtn) {
          var minSelections = parseInt(nextBtn.dataset.minSelections, 10) || 1;
          var checkedCount = block.querySelectorAll('.qpg-answer__input:checked').length;
          nextBtn.disabled = checkedCount < minSelections;
        }
      }
    }

    function finalizeQuestionAnswer(block, idx) {
      var checkedInputs = Array.prototype.slice.call(block.querySelectorAll('.qpg-answer__input:checked'));
      var selections = checkedInputs.map(function (inp) {
        var label = inp.closest('.qpg-arow');
        return {
          value: label.dataset.answerValue,
          text: label.dataset.answerText,
          severity: label.dataset.answerSeverity,
          route_flag: label.dataset.answerRouteFlag,
          previous_brand: label.dataset.answerPreviousBrand || null
        };
      });

      answers[idx] = {
        type: 'quiz-question',
        block_id: block.dataset.blockId,
        selections: selections,
        is_q5: block.dataset.isQ5 === 'true',
        is_q7: block.dataset.isQ7 === 'true'
      };

      updatePersonalizationFromQuestion(block, selections);
      persist();
      log('Question answered', { idx: idx, selections: selections });
    }

    function updatePersonalizationFromQuestion(block, selections) {
      var pkey = block.dataset.personalizationKey;
      if (pkey && selections.length > 0) {
        personalization[pkey] = selections[0].value;
        personalization[pkey + '_label'] = selections[0].text;
      }

      // Symptoms question: top-2 by severity, human labels
      if (block.dataset.isQ5 === 'true') {
        personalization.q5_symptoms_top_2 = pickTopTwoSymptoms(selections);
      }

      // Already-tried question: previous brand + category (drives interstitial + order stamp)
      if (block.dataset.isQ7 === 'true') {
        personalization.previous_brand = '';
        for (var i = 0; i < selections.length; i++) {
          if (selections[i].previous_brand) {
            personalization.previous_brand = selections[i].previous_brand;
            break;
          }
        }
        var q7vals = selections.map(function (s) { return s.value; });
        var q7flags = selections.map(function (s) { return s.route_flag; });
        var namedBrands = ['yumove', 'cosequin', 'other_supplement', 'palatability_fail'];
        var cat = 'default';
        if (q7vals.some(function (v) { return namedBrands.indexOf(v) !== -1; })) {
          cat = 'tried_failed';
        } else if (q7vals.indexOf('supermarket_glucosamine') !== -1) {
          cat = 'single_ingredient';
        } else if (q7flags.indexOf('drug_interaction') !== -1) {
          cat = 'meds_only';
        } else if (q7vals.indexOf('working_fine') !== -1) {
          cat = 'working_fine';
        } else if (q7vals.indexOf('nothing_yet') !== -1) {
          cat = 'nothing_yet';
        }
        personalization.tried_category = cat;
      }

      // Vet medication flag: any answer on any question carrying drug_interaction
      personalization.drug_interaction_flag = collectAllRouteFlags().indexOf('drug_interaction') !== -1;
    }

    function pickTopTwoSymptoms(selections) {
      var severityWeight = { severe: 2, moderate: 1, mild: 1, pre_symptom: 0, none: 0 };
      var sorted = selections.slice().sort(function (a, b) {
        return (severityWeight[b.severity] || 0) - (severityWeight[a.severity] || 0);
      });
      return sorted.slice(0, 2).map(function (s) { return s.text; }).join(' | ');
    }

    /* ======== Text input (dog name) ======== */

    function bindTextInput(block, idx) {
      var input = block.querySelector('[data-text-input]');
      var nextBtn = block.querySelector('.qpg-next');
      if (!input) return;

      input.addEventListener('input', function () {
        if (nextBtn) nextBtn.disabled = input.value.trim().length === 0;
      });

      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && input.value.trim().length > 0) {
          e.preventDefault();
          finalizeTextInput(block, idx, input);
          advanceFrom(idx);
        }
      });

      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          if (input.value.trim().length === 0) return;
          finalizeTextInput(block, idx, input);
          advanceFrom(idx);
        });
      }
    }

    function finalizeTextInput(block, idx, input) {
      var pkey = input.dataset.personalizationTarget || 'dog_name';
      var val = input.value.trim();
      val = val.charAt(0).toUpperCase() + val.slice(1);
      answers[idx] = { type: 'text-input', block_id: block.dataset.blockId, value: val };
      personalization[pkey] = val;
      persist();
      log('Text input', { key: pkey, value: val });
    }

    /* ======== Dual select (sex + neuter, optional block) ======== */

    function bindDualSelect(block, idx) {
      var groupA = block.querySelectorAll('[data-dual-group="a"]');
      var groupB = block.querySelectorAll('[data-dual-group="b"]');
      var groupBWrap = block.querySelector('[data-dual-group-b]');

      groupA.forEach(function (label) {
        var input = label.querySelector('.qpg-answer__input');
        input.addEventListener('change', function () {
          groupA.forEach(function (l) { l.classList.remove('qpg-arow--selected'); });
          label.classList.add('qpg-arow--selected');
          if (groupBWrap) groupBWrap.style.display = '';
          personalization.sex = label.dataset.dualValue;
          personalization.pronoun = label.dataset.dualPronoun || 'he';
        });
      });

      groupB.forEach(function (label) {
        var input = label.querySelector('.qpg-answer__input');
        input.addEventListener('change', function () {
          groupB.forEach(function (l) { l.classList.remove('qpg-arow--selected'); });
          label.classList.add('qpg-arow--selected');
          personalization.neuter = label.dataset.dualValue;
          answers[idx] = {
            type: 'dual-select',
            block_id: block.dataset.blockId,
            sex: personalization.sex,
            pronoun: personalization.pronoun,
            neuter: personalization.neuter
          };
          persist();
          setTimeout(function () { advanceFrom(idx); }, parseInt(getCssVar('--auto-advance-delay'), 10) || 360);
        });
      });
    }

    /* ======== Interstitial ======== */

    function bindInterstitial(block, idx) {
      var ctaWrap = block.querySelector('[data-interstitial-cta]');
      var nextBtn = block.querySelector('.qpg-next');

      block.addEventListener('quiz:block-shown', function () {
        var pause = parseInt(getCssVar('--interstitial-read-pause'), 10) || 2000;
        setTimeout(function () {
          if (ctaWrap) {
            ctaWrap.style.transition = 'opacity 400ms var(--e-soft)';
            ctaWrap.style.opacity = '1';
            ctaWrap.style.pointerEvents = '';
          }
        }, pause);
      }, { once: true });

      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          answers[idx] = { type: 'interstitial', block_id: block.dataset.blockId, viewed: true };
          persist();
          advanceFrom(idx);
        });
      }
    }

    /**
     * Pick the interstitial copy variant that matches the already-tried answer.
     * Reveals the matching [data-variant] block, hides the rest, falls back to default.
     */
    function selectInterstitialVariant(block) {
      var key = deriveInterstitialVariantKey();
      var variants = Array.prototype.slice.call(block.querySelectorAll('.qpg-interstitial__variant'));
      if (variants.length === 0) return;

      var chosen = null;
      variants.forEach(function (v) { if (v.dataset.variant === key) chosen = v; });
      if (!chosen) {
        variants.forEach(function (v) { if (v.dataset.variant === 'default') chosen = v; });
      }

      variants.forEach(function (v) {
        if (v === chosen) {
          v.hidden = false;
          applyPersonalization(v);
        } else {
          v.hidden = true;
        }
      });

      log('Interstitial variant selected:', key, '| chosen:', chosen ? chosen.dataset.variant : 'NONE');
    }

    function deriveInterstitialVariantKey() {
      var q7 = null;
      Object.keys(answers).forEach(function (k) {
        var a = answers[k];
        if (a && a.type === 'quiz-question' && a.is_q7) q7 = a;
      });
      if (!q7 || !q7.selections) return 'default';

      var values = q7.selections.map(function (s) { return s.value; });
      var flags = q7.selections.map(function (s) { return s.route_flag; });

      // LEGAL: named-brand answers (yumove, cosequin, ...) intentionally share the
      // brand-agnostic "tried a supplement" variant. No competitor-specific copy exists.
      var namedBrandValues = ['yumove', 'cosequin', 'other_supplement'];
      if (values.some(function (v) { return namedBrandValues.indexOf(v) !== -1; })) return 'other_supplement';
      if (values.indexOf('supermarket_glucosamine') !== -1) return 'supermarket_glucosamine';
      if (values.indexOf('palatability_fail') !== -1) return 'palatability_fail';
      if (flags.indexOf('drug_interaction') !== -1) return 'meds_only';
      if (values.indexOf('working_fine') !== -1) return 'working_fine';
      if (values.indexOf('nothing_yet') !== -1) return 'nothing_yet';
      return 'default';
    }

    /* ======== Affirmation (auto-advancing) ======== */

    function bindAffirmation(block, idx) {
      block.addEventListener('quiz:block-shown', function () {
        var duration = parseInt(block.dataset.autoAdvanceMs, 10) || 1200;
        setTimeout(function () { advanceFrom(idx); }, duration);
      }, { once: true });
    }

    /* ======== Loading screen ======== */

    function bindLoading(block, idx) {
      block.addEventListener('quiz:block-shown', function () {
        var totalDuration = parseInt(block.dataset.totalDuration, 10) || 2500;
        var states = Array.prototype.slice.call(block.querySelectorAll('.qpg-loading__state'));
        var stateCount = Math.max(1, states.length);
        var stateDuration = totalDuration / stateCount;
        var currentState = 0;

        var interval = setInterval(function () {
          currentState++;
          if (currentState >= stateCount) { clearInterval(interval); return; }
          states.forEach(function (s, i) {
            s.classList.toggle('qpg-loading__state--active', i === currentState);
          });
        }, stateDuration);

        setTimeout(function () { completeAndRedirect(); }, totalDuration);
      }, { once: true });
    }

    /* ======== Navigation ======== */

    function advanceFrom(idx) {
      var nextIdx = idx + 1;
      if (nextIdx >= blocks.length) {
        completeAndRedirect();
        return;
      }
      goToBlock(nextIdx);
    }

    function goToBlock(idx) {
      if (idx < 0 || idx >= blocks.length) return;
      var prevBlock = blocks[currentIdx];
      var nextBlock = blocks[idx];

      if (currentIdx >= 0 && history[history.length - 1] !== currentIdx) {
        history.push(currentIdx);
      }

      if (prevBlock) {
        prevBlock.classList.remove('quiz-pg-block--active');
        prevBlock.classList.add('quiz-pg-block--exiting');
        setTimeout(function () { prevBlock.classList.remove('quiz-pg-block--exiting'); }, 400);
      }

      currentIdx = idx;
      nextBlock.classList.add('quiz-pg-block--active');
      nextBlock.classList.add('quiz-pg-block--entering');
      setTimeout(function () { nextBlock.classList.remove('quiz-pg-block--entering'); }, 400);

      applyPersonalization(nextBlock);

      // Interstitial variant runs on every arrival (including after going back
      // and forward), so it always reflects the latest already-tried answer.
      if (nextBlock.dataset.blockType === 'interstitial') {
        selectInterstitialVariant(nextBlock);
      }

      nextBlock.dispatchEvent(new CustomEvent('quiz:block-shown', { detail: { idx: idx } }));

      updateChrome();
      persist();
      trackProgress(nextBlock);

      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    function goBack() {
      if (history.length === 0) return;
      var prevIdx = history.pop();
      // Skip back over affirmations (not interactive)
      while (prevIdx >= 0 && blocks[prevIdx].dataset.blockType === 'affirmation' && history.length > 0) {
        prevIdx = history.pop();
      }
      if (prevIdx >= 0) {
        var prevBlock = blocks[currentIdx];
        var nextBlock = blocks[prevIdx];
        if (prevBlock) prevBlock.classList.remove('quiz-pg-block--active');
        currentIdx = prevIdx;
        nextBlock.classList.add('quiz-pg-block--active');
        applyPersonalization(nextBlock);
        updateChrome();
        persist();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }

    function updateChrome() {
      if (!headerEl) return;

      var current = blocks[currentIdx];
      var type = current ? current.dataset.blockType : '';
      var isHero = currentIdx <= 0;
      var isLoading = type === 'loading';
      var isAffirmation = type === 'affirmation';

      // Hero slide carries its own logo; the sticky header starts on slide 2.
      if (isHero) {
        headerEl.style.display = 'none';
        return;
      }
      headerEl.style.display = '';

      if (backBtn) {
        var canGoBack = history.length > 0 && !isLoading && !isAffirmation;
        backBtn.style.display = canGoBack ? '' : 'none';
      }

      var stepBlocks = blocks.filter(function (b) { return STEP_TYPES.indexOf(b.dataset.blockType) !== -1; });
      var total = stepBlocks.length;
      var stepIdx = stepBlocks.indexOf(current);

      // On non-step slides (interstitial, affirmation, loading) hold the last step reached.
      if (stepIdx < 0) {
        var reached = 0;
        for (var i = 0; i <= currentIdx && i < blocks.length; i++) {
          if (STEP_TYPES.indexOf(blocks[i].dataset.blockType) !== -1) reached++;
        }
        stepIdx = reached - 1;
      }

      if (stepLabel) {
        stepLabel.textContent = (stepIdx >= 0 && !isLoading) ? (stepIdx + 1) + ' / ' + total : '\u00a0';
      }
      if (progressFill && total > 0) {
        var pct = isLoading ? 100 : (stepIdx >= 0 ? ((stepIdx + 1) / total) * 100 : 0);
        progressFill.style.width = pct + '%';
      }
    }

    function applyPersonalization(block) {
      var personalizableEls = block.querySelectorAll('[data-personalize]');
      personalizableEls.forEach(function (el) {
        var template = el.getAttribute('data-personalize');
        if (!template) return;
        el.innerHTML = renderTemplate(template);
      });

      // Rich HTML bodies can't live in an attribute; tokenize in place (idempotent).
      var richEls = block.querySelectorAll('.qpg-interstitial__body, .qpg-hero__proof-text');
      richEls.forEach(function (el) {
        if (el.innerHTML.indexOf('{') === -1) return;
        el.innerHTML = renderTemplate(el.innerHTML);
      });
    }

    function renderTemplate(str) {
      if (!str) return '';
      return str.replace(/\{(\w+)\}/g, function (match, key) {
        return personalization[key] !== undefined && personalization[key] !== '' && personalization[key] !== false
          ? escapeHtml(String(personalization[key]))
          : match;
      });
    }

    /* ======== Persistence ======== */

    function persist() {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify({
          sessionId: sessionId,
          startedAt: startedAt,
          currentIdx: currentIdx,
          history: history,
          answers: answers,
          personalization: personalization,
          blockCount: blocks.length
        }));
      } catch (e) {
        log('Persist failed', e);
      }
    }

    /**
     * Restore a session from sessionStorage. Returns true if a valid session was
     * resumed (currentIdx >= 0 and the matching block is now active), else false.
     * Never resumes onto a non-step slide (loader would redirect on show).
     */
    function rehydrate() {
      try {
        var raw = sessionStorage.getItem(storageKey);
        if (!raw) return false;
        var state = JSON.parse(raw);
        if (!state || !state.sessionId) return false;
        // Expire after 2h, or if the block set changed since the state was saved.
        if (Date.now() - state.startedAt > 7200000 || state.blockCount !== blocks.length) {
          sessionStorage.removeItem(storageKey);
          return false;
        }
        var idx = typeof state.currentIdx === 'number' ? state.currentIdx : 0;
        if (idx < 0 || idx >= blocks.length) return false;
        while (idx > 0 && STEP_TYPES.indexOf(blocks[idx].dataset.blockType) === -1) idx--;

        sessionId = state.sessionId;
        startedAt = state.startedAt;
        currentIdx = idx;
        history = (state.history || []).filter(function (h) { return h < idx; });
        answers = state.answers || {};
        personalization = Object.assign(personalization, state.personalization || {});
        log('Rehydrated', { currentIdx: currentIdx });

        blocks.forEach(function (b) { b.classList.remove('quiz-pg-block--active'); });
        blocks[currentIdx].classList.add('quiz-pg-block--active');
        return true;
      } catch (e) {
        log('Rehydrate failed', e);
        return false;
      }
    }

    /* ======== Completion ======== */

    function completeAndRedirect() {
      if (isRedirecting) return;
      isRedirecting = true;

      var url = buildDestinationUrl();
      log('Redirecting to', url);

      try {
        var trackableBlocks = blocks.filter(function (b) {
          return TRACKABLE_STEP_TYPES.indexOf(b.dataset.blockType) !== -1;
        });
        sendTracking({
          completed: true,
          stepId: 'completed',
          stepIndex: trackableBlocks.length,
          totalSteps: trackableBlocks.length
        });
      } catch (e) { log('sendTracking (complete) failed', e); }
      try { writeCartAttribute(); } catch (e) { log('writeCartAttribute failed', e); }

      try { sessionStorage.removeItem(storageKey); } catch (e) {}
      setTimeout(function () { window.location.href = url; }, 50);
    }

    function buildDestinationUrl() {
      var url;
      var isRelative = offerUrl && offerUrl.charAt(0) === '/' && offerUrl.charAt(1) !== '/';
      try {
        url = new URL(offerUrl, window.location.origin);
      } catch (e) {
        url = new URL('/', window.location.origin);
      }

      if (forwardUtm) {
        var incoming = new URLSearchParams(window.location.search);
        ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (k) {
          var v = incoming.get(k);
          if (v) url.searchParams.set(k, v);
        });
      }

      if (isRelative) return url.pathname + url.search + url.hash;
      return url.toString();
    }

    /**
     * POST the current quiz state to the configured Google Apps Script endpoint.
     * Called on every step (drop-off tracking) and again at completion. The
     * endpoint upserts by quiz_id. Key set is identical to v1 so the Sheet
     * columns do not shift; removed-question keys are sent empty.
     * Transport intentionally matches the deployed v1 receiver (doPost).
     */
    function sendTracking(opts) {
      opts = opts || {};
      var endpoint = root.dataset.trackingEndpoint || '';
      if (!endpoint) { log('No tracking endpoint set, skipping sendTracking'); return; }

      var incoming = new URLSearchParams(window.location.search);
      var payload = {
        quiz_id: sessionId,
        started_at: new Date(startedAt).toISOString(),
        completed: !!opts.completed,
        last_step_id: opts.stepId || '',
        step_index: (opts.stepIndex != null ? opts.stepIndex : ''),
        total_steps: (opts.totalSteps != null ? opts.totalSteps : ''),
        submitted_at: opts.completed ? new Date().toISOString() : '',
        result_type: '',
        dog_name: personalization.dog_name || '',
        age: personalization.age || '',
        breed: '',
        weight_bracket: personalization.weight_bracket || '',
        sex: personalization.sex || '',
        neuter: personalization.neuter || '',
        q5_symptoms_top_2: personalization.q5_symptoms_top_2 || '',
        q6_timeframe: '',
        q9_number: '',
        q9_anchor: '',
        previous_brand: personalization.previous_brand || '',
        tried_category: personalization.tried_category || '',
        value_anchor: personalization.value_anchor || '',
        drug_interaction_flag: !!personalization.drug_interaction_flag,
        page_url: window.location.href,
        utm_source: incoming.get('utm_source') || '',
        utm_medium: incoming.get('utm_medium') || '',
        utm_campaign: incoming.get('utm_campaign') || ''
      };

      var body = JSON.stringify(payload);
      var sent = false;
      if (navigator.sendBeacon) {
        try {
          var blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
          sent = navigator.sendBeacon(endpoint, blob);
        } catch (e) { sent = false; }
      }
      if (!sent) {
        try {
          fetch(endpoint, {
            method: 'POST',
            mode: 'no-cors',
            keepalive: true,
            headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
            body: body
          });
        } catch (e) { log('fetch tracking fallback failed', e); }
      }
      log('Tracking sent', { quiz_id: payload.quiz_id, step: payload.last_step_id, completed: payload.completed });
    }

    function trackProgress(block) {
      if (!block) return;
      var type = block.dataset.blockType;
      if (TRACKABLE_STEP_TYPES.indexOf(type) === -1) return;

      var trackableBlocks = blocks.filter(function (b) {
        return TRACKABLE_STEP_TYPES.indexOf(b.dataset.blockType) !== -1;
      });
      var stepNum = trackableBlocks.indexOf(block) + 1;
      var total = trackableBlocks.length;
      var stepId = block.dataset.blockId || type;

      try {
        sendTracking({ completed: false, stepId: stepId, stepIndex: stepNum, totalSteps: total });
      } catch (e) { log('trackProgress failed', e); }
    }

    /**
     * Stamp the Shopify cart with the quiz data as cart attributes. These ride
     * to the order and Klaviyo ingests them on "Placed Order". Same key set as v1
     * minus the removed questions; empty values are dropped.
     */
    function writeCartAttribute() {
      var attrs = {
        quiz_id: sessionId,
        quiz_version: 'v2',
        quiz_dog_name: personalization.dog_name || '',
        quiz_age: personalization.age_label || personalization.age || '',
        quiz_weight: personalization.weight_bracket || '',
        quiz_sex: personalization.sex || '',
        quiz_symptoms: personalization.q5_symptoms_top_2 || '',
        quiz_tried_before: personalization.previous_brand || '',
        quiz_tried_category: personalization.tried_category || '',
        quiz_motivation: personalization.value_anchor || '',
        quiz_on_vet_meds: personalization.drug_interaction_flag ? 'yes' : 'no'
      };

      var attributes = {};
      Object.keys(attrs).forEach(function (k) {
        if (attrs[k] !== '' && attrs[k] !== null && attrs[k] !== undefined) attributes[k] = attrs[k];
      });

      try {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: attributes }),
          keepalive: true
        });
        log('Cart attributes written', attributes);
      } catch (e) {
        log('cart attribute write failed', e);
      }
    }

    function collectAllRouteFlags() {
      var flags = [];
      Object.keys(answers).forEach(function (k) {
        var a = answers[k];
        if (!a || a.type !== 'quiz-question') return;
        (a.selections || []).forEach(function (s) {
          if (s.route_flag && s.route_flag !== 'none' && flags.indexOf(s.route_flag) === -1) {
            flags.push(s.route_flag);
          }
        });
      });
      return flags;
    }

    /* ======== Utilities ======== */

    function generateSessionId() {
      return 'pg2_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
    }

    function getCssVar(name) {
      return getComputedStyle(root).getPropertyValue(name).trim();
    }

    function escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();